import {
  COMPETITIONS,
  PAPERS,
  isPartDone,
  isSkipped,
  isSubmitted,
} from "./sampleTranslateData";

export const KIND_LABEL = {
  mcq: "MCQ",
  input: "Input",
  open_ended: "Open ended",
};

export function partIds(paper) {
  const ids = ["cover"];
  paper.sections.forEach((section) => {
    ids.push(`section:${section.id}`);
    section.tasks.forEach((task) => ids.push(`task:${task.id}`));
  });
  return ids;
}

export function questionCount(gradeId) {
  const paper = PAPERS[gradeId];
  if (!paper) return 0;
  return paper.sections.reduce((sum, section) => sum + section.tasks.length, 0);
}

export function doneCount(gradeId, language) {
  const paper = PAPERS[gradeId];
  if (!paper) return 0;
  return partIds(paper).filter((part) => isPartDone(gradeId, language, part)).length;
}

export function totalParts(gradeId) {
  const paper = PAPERS[gradeId];
  return paper ? partIds(paper).length : 0;
}

export function statusLabel(done, total) {
  if (done === 0) return "Not started";
  if (done === total) return "Complete";
  return "In progress";
}

export function findPaperContext(competitionId, gradeId) {
  const competition = COMPETITIONS.find((item) => item.id === competitionId);
  const grade = competition?.grades.find((item) => item.id === gradeId);
  const paper = PAPERS[gradeId];
  if (!competition || !grade || !paper) return null;
  return { competition, grade, paper };
}

export function partState(gradeId, language, part) {
  if (isSkipped(gradeId, language, part)) return "Not needed";
  if (isSubmitted(gradeId, language, part)) return "Submitted";
  return "Open";
}

function textValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return value.content || "";
}

export function mapCollectionTasks(payload) {
  const body = payload?.collection ? payload : payload?.data;
  if (!body?.collection || !Array.isArray(body.sections)) return null;

  const drafts = {};
  const skipped = {};
  const done = {};
  const coverTranslation = textValue(body.cover_page?.translation);
  drafts["cover|body"] = coverTranslation;
  if (coverTranslation) done.cover = true;

  const sections = body.sections.map((section, index) => {
    const sectionPart = `section:${section.id}`;
    const sectionTranslation = textValue(section.translation);
    drafts[`${sectionPart}|body`] = sectionTranslation;
    if (sectionTranslation) done[sectionPart] = true;

    const tasks = (section.tasks || []).map((task) => {
      const taskPart = `task:${task.id}`;
      const translation = task.question?.translation;
      drafts[`${taskPart}|label`] = translation?.label || "";
      drafts[`${taskPart}|content`] =
        typeof translation === "string" ? translation : translation?.content || "";
      if (task.no_translation_needed) skipped[task.id] = true;
      if (task.is_done) done[taskPart] = true;

      const answers = (task.answers || []).map((answer, answerIndex) => {
        const english = answer.english_content ?? answer.english_label ?? "";
        const translated = answer.translated_content ?? answer.translated_label ?? "";
        drafts[`${taskPart}|answer:${answerIndex}`] = translated || "";
        return {
          id: answer.id,
          position: answer.position || answerIndex + 1,
          english,
          rich: task.answer_type === "mcq",
        };
      });

      return {
        id: task.id,
        label: task.question?.english?.label || task.identifier,
        identifier: task.identifier,
        kind: task.answer_type,
        englishLabel: task.question?.english?.label || "",
        englishContent: task.question?.english?.content || "",
        answers,
      };
    });

    return {
      id: section.id,
      name: `Section ${index + 1}`,
      english: section.english || "",
      tasks,
    };
  });

  return {
    collectionName: body.collection.name || "",
    completed: body.grade_language?.completed_questions ?? 0,
    total: body.grade_language?.total_questions ?? 0,
    finalized: Boolean(body.is_finalized || body.grade_language?.is_finalized),
    paper: {
      cover: body.cover_page?.english || "",
      sections,
    },
    drafts,
    skipped,
    done,
  };
}

export function findTask(paper, taskId) {
  for (const section of paper?.sections || []) {
    const task = section.tasks.find((item) => String(item.id) === String(taskId));
    if (task) return task;
  }
  return null;
}

export function instructionsPayload(workspace, gradeId, languageId) {
  return {
    translation_assignment_grade_id: Number(gradeId),
    language_id: Number(languageId),
    cover_page: workspace.drafts["cover|body"] || "",
    sections: workspace.paper.sections.map((section) => ({
      section_id: section.id,
      description: workspace.drafts[`section:${section.id}|body`] || "",
    })),
  };
}

export function taskDonePayload(task, drafts, languageId, noTranslationNeeded) {
  const part = `task:${task.id}`;
  return {
    language_id: Number(languageId),
    no_translation_needed: !!noTranslationNeeded,
    question: {
      label: drafts[`${part}|label`] || "",
      content: drafts[`${part}|content`] || "",
    },
    answers: (task.answers || []).map((answer, index) => ({
      task_answer_id: answer.id,
      content: drafts[`${part}|answer:${index}`] || "",
    })),
  };
}
